import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useOsmTiles } from '../hooks/use-osm-tiles';

// Hydranten und Löschwasserstellen aus OpenStreetMap (gleiche Datenbasis wie OpenFireMap)
// für den sichtbaren Kartenausschnitt. Das Backend fragt die Overpass API an (eindeutige
// Kennung, Zwischenspeicher, Ausweich-Server); direkt aus dem Browser lehnt Overpass teils ab.
// Geladen wird in Kacheln (useOsmTiles): Bekanntes erscheint sofort, nur neue Kacheln werden angefragt.

export const HYDRANT_MIN_ZOOM = 15;
// Mehr Marker machen die Karte träge; dann wird nur ein Teil angezeigt
const MAX_MARKERS = 1000;

const HYDRANT_TYPES = {
  underground: { label: 'Unterflurhydrant', short: 'U' },
  pillar: { label: 'Überflurhydrant', short: 'Ü' },
  wall: { label: 'Wandhydrant', short: 'W' },
  pipe: { label: 'Steigleitung', short: 'S' },
};

const WATER_SOURCES = {
  water_tank: { label: 'Löschwasserbehälter', short: 'B' },
  suction_point: { label: 'Saugstelle', short: 'S' },
  fire_water_pond: { label: 'Löschwasserteich', short: 'T' },
};

export const describeWaterSource = (tags = {}) => {
  // amenity=fire_hydrant ist die ältere Erfassung desselben Objekts
  if (tags.emergency === 'fire_hydrant' || tags.amenity === 'fire_hydrant') {
    const type = HYDRANT_TYPES[tags['fire_hydrant:type']] || { label: 'Hydrant', short: 'H' };
    return { ...type, isHydrant: true };
  }
  return { ...(WATER_SOURCES[tags.emergency] || { label: 'Löschwasserstelle', short: 'L' }), isHydrant: false };
};

const iconCache = new Map();

const waterIcon = (tags) => {
  const { short, isHydrant } = describeWaterSource(tags);
  const key = `${short}-${isHydrant}`;
  if (!iconCache.has(key)) {
    // Hydranten als Kreis, sonstige Löschwasserstellen als Quadrat
    const shape = isHydrant
      ? '<circle cx="11" cy="11" r="9" fill="#2563eb" stroke="white" stroke-width="2"/>'
      : '<rect x="2" y="2" width="18" height="18" rx="2" fill="#0e7490" stroke="white" stroke-width="2"/>';
    iconCache.set(key, L.divIcon({
      html: `<svg width="22" height="22" viewBox="0 0 22 22" xmlns="http://www.w3.org/2000/svg">${shape}
        <text x="11" y="15" text-anchor="middle" font-size="10" font-weight="700" font-family="sans-serif" fill="white">${short}</text></svg>`,
      className: 'hydrant-marker',
      iconSize: [22, 22],
      iconAnchor: [11, 11],
      popupAnchor: [0, -11],
    }));
  }
  return iconCache.get(key);
};

const DETAIL_TAGS = [
  ['fire_hydrant:diameter', 'Durchmesser', v => `DN ${v}`],
  ['fire_hydrant:pressure', 'Druck', v => (v === 'suction' ? 'Saugbetrieb' : `${v} bar`)],
  ['flow_rate', 'Leistung', v => v],
  ['water_volume', 'Volumen', v => `${v} m³`],
  ['ref', 'Nummer', v => v],
  ['operator', 'Betreiber', v => v],
  ['fire_hydrant:position', 'Lage', v => ({ sidewalk: 'Gehweg', lane: 'Fahrbahn', parking_lot: 'Parkplatz', green: 'Grünfläche' }[v] || v)],
  ['description', 'Hinweis', v => v],
];

const WaterSourcePopup = ({ tags }) => {
  const { label } = describeWaterSource(tags);
  return (
    <div className="font-sans text-sm">
      <strong>{label}</strong>
      {DETAIL_TAGS.filter(([key]) => tags[key]).map(([key, title, format]) => (
        <p key={key} className="mt-1">{title}: {format(tags[key])}</p>
      ))}
      <p className="mt-2 text-xs text-muted-foreground">Quelle: OpenStreetMap</p>
    </div>
  );
};

export const HydrantLayer = ({ enabled, onStatusChange }) => {
  const nodes = useOsmTiles({
    layer: 'hydranten',
    enabled,
    minZoom: HYDRANT_MIN_ZOOM,
    maxItems: MAX_MARKERS,
    onStatusChange,
  });

  if (!enabled) return null;

  return nodes.map(node => (
    <Marker
      key={node.id}
      position={[node.lat, node.lon]}
      icon={waterIcon(node.tags)}
      interactive={true}
      keyboard={false}
    >
      <Popup>
        <WaterSourcePopup tags={node.tags || {}} />
      </Popup>
    </Marker>
  ));
};

export default HydrantLayer;
